import { useEffect, useState, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import useSWR, { mutate } from "swr";
import AllDataUser from "../hookData";
import {
  claimPendingListAction,
  claimPendingListResetAction,
} from "../../Action/claimListPendingAction";
import { baseUrl } from "../../Config/Api";
import { readAllNotificationsClaim } from "../../Services/ClaimCashless";
import { isTokenExpired, normalizeBearerToken } from "./sseAuth";

export const useNotificationsClaimPending = () => {
  const isAuthenticated = useSelector((state) => state.isLogin.isLogin);
  const { user } = AllDataUser();
  const dispatch = useDispatch();
  const [badgeCount, setBadgeCount] = useState(0);

  const clinicId = user?.clinicId;
  const userId = user?.user_id;

  const defaultSearchDataClaim = useMemo(() => {
    if (!isAuthenticated || !clinicId || !userId) return null;
    return {
      clinicId,
      admissionStartDate: null,
      admissionEndDate: null,
      claimStatus: 2,
      rocNo: "",
      cardNumb: "",
      pageSize: 20,
      page: 1,
      language: "EN",
      key: "",
      id: userId,
    };
  }, [isAuthenticated, clinicId, userId]);

  const { data, isLoading } = useSWR("claimListKey", {
    fetcher: () => null,
    revalidateOnFocus: false,
  });

  const refetchNotifications = useCallback(() => {
    if (isAuthenticated && defaultSearchDataClaim) {
      dispatch(claimPendingListAction({ ...defaultSearchDataClaim }));
    }
  }, [isAuthenticated, defaultSearchDataClaim, dispatch]);

  useEffect(() => {
    if (isAuthenticated && defaultSearchDataClaim) {
      dispatch(claimPendingListAction({ ...defaultSearchDataClaim }));
    }
  }, [isAuthenticated, defaultSearchDataClaim, dispatch]);

  useEffect(() => {
    if (!isAuthenticated || !defaultSearchDataClaim) return;

    let eventSource = null;
    let reconnectTimeout = null;
    let isMounted = true;

    const connectSSE = () => {
      if (!isMounted) return;

      const rawToken = localStorage.getItem("Authorization") || "";
      const cleanToken = normalizeBearerToken(rawToken);

      if (!cleanToken || isTokenExpired(cleanToken)) {
        console.warn("Skipping SSE connection: missing or expired JWT token.");
        return;
      }

      const sseUrl = `${baseUrl}/claim/notifications/sse?token=${encodeURIComponent(
        cleanToken
      )}`;

      try {
        eventSource = new EventSource(sseUrl, { withCredentials: true });

        eventSource.onopen = () => {
          if (defaultSearchDataClaim) {
            dispatch(claimPendingListAction({ ...defaultSearchDataClaim }));
          }
        };

        eventSource.onmessage = (event) => {
          try {
            if (!event || !event.data) return;
            const parsedData = JSON.parse(event.data);
            if (parsedData && parsedData.badgeCount !== undefined) {
              setBadgeCount(parsedData.badgeCount);
            }
            if (defaultSearchDataClaim) {
              dispatch(claimPendingListAction({ ...defaultSearchDataClaim }));
            }
          } catch (e) {
            console.error("Error parsing SSE message:", e);
          }
        };

        eventSource.onerror = (err) => {
          console.error("SSE connection error:", err);
          if (eventSource) {
            eventSource.close();
            eventSource = null;
          }

          const nextToken = normalizeBearerToken(
            localStorage.getItem("Authorization") || ""
          );
          if (isMounted && nextToken && !isTokenExpired(nextToken)) {
            reconnectTimeout = setTimeout(() => {
              connectSSE();
            }, 5000);
          }
        };
      } catch (err) {
        console.error("Failed to establish SSE connection:", err);
        const nextToken = normalizeBearerToken(
          localStorage.getItem("Authorization") || ""
        );
        if (isMounted && nextToken && !isTokenExpired(nextToken)) {
          reconnectTimeout = setTimeout(() => {
            connectSSE();
          }, 5000);
        }
      }
    };

    connectSSE();

    return () => {
      isMounted = false;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      if (eventSource) {
        eventSource.close();
      }
    };
  }, [isAuthenticated, defaultSearchDataClaim, dispatch]);

  return {
    claimList: data || [],
    isLoading,
    badgeCount,
    refetchNotifications,
  };
};

//--refectch claim List Pending--///
export const useRefetchClaimListPending = (isRefetch, setTriggerRefetch) => {
  const { user } = AllDataUser();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state) => state.isLogin.isLogin);
  const claimPendingList = useSelector((state) => state.claimListPending);
  const defaultSearchDataClaim = isAuthenticated &&
    user && {
      clinicId: user.clinicId,
      admissionStartDate: null,
      admissionEndDate: null,
      claimStatus: 2,
      rocNo: "",
      cardNumb: "",
      pageSize: 20,
      page: 1,
      language: "EN",
      key: "",
      id: user.user_id,
    };

  useEffect(() => {
    if (isAuthenticated && isRefetch) {
      console.log("run...refecth");
      dispatch(claimPendingListAction({ ...defaultSearchDataClaim }));
    }

    return () => {
      dispatch(claimPendingListResetAction());
    };
  }, [isAuthenticated, isRefetch]);

  useEffect(() => {
    if (isRefetch && claimPendingList.isFetching) {
      mutate("claimListKey", claimPendingList.data, false);
      setTriggerRefetch(false);
    }
  }, [isRefetch, claimPendingList]);
};

export const useReadAllNotificationsOnMount = () => {
  const { user } = AllDataUser();
  const userId = user && user.user_id;

  useEffect(() => {
    if (userId) {
      readAllNotificationsClaim(userId).catch((err) => {
        console.error("Failed to mark all notifications as read:", err);
      });
    }
  }, [userId]);
};
